import {
  faCircleCheck,
  faClockRotateLeft,
  faFileImport,
  faLink,
  faPuzzlePiece,
  faSliders,
  faTriangleExclamation,
  faWandMagicSparkles,
} from "@fortawesome/pro-solid-svg-icons";
import { computed, markRaw } from "vue";
import DialogConfirm from "@/components/dialogs/DialogConfirm.vue";
import { usePropertyBuilder } from "@/components/views/config/helpers";
import type { IUseMods, ModRelation, ModTag } from "@/components/views/mods";
import { formatModSource } from "@/components/views/mods";
import type { SteamWorkshopItem, ValheimModsInfo, ValheimProfileNamespace, ValheimSelectedMod } from "@/graphql";
import { SteamWorkshopItemType } from "@/graphql";
import { useDialogStore } from "@/store/dialog";
import { useServiceStore } from "@/store/service";
import { useValheimModsStore } from "@/store/valheimMods";
import { COMPAT, classify as classifyMod, formatDay, releaseDay } from "@/util/valheimModCompat";
import { i18n } from "../i18n";
import { commonMenuItems } from "./common";
import defaultProfile, { asyncRoute } from "./default";
import { type ConfigSection, type GameProfile, type Menu, MenuItem, type ProfileCtx } from "./types";

const { t } = i18n.global;

const valheimPlusMenuItem = new MenuItem(
  "valheim-plus",
  faSliders,
  asyncRoute(() => import("../components/views/config/valheim/ValheimPlusConf.vue")),
);

/** Repo-key prefix of every Thunderstore mod package — the selection is stored as repo keys. */
export const MOD_PREFIX = "modulus_thunderstore_valheim_";

const THUNDERSTORE_URL = "https://valheim.thunderstore.io/c/valheim/";
/** Same placeholder Project Zomboid uses for a mod it has no catalogue row for yet. */
const DEFAULT_MOD_IMAGE = "https://cdn.g-portal.com/images/no-map.png";

const configSections: ConfigSection[] = [
  { component: "ConfSection", props: { name: "basic" } },
  // The world dropdown + SavegameName, with the selected world's metadata rendered live beneath
  // them (TR-5318). Its own section so the custom behaviour has somewhere to live.
  { component: "ValheimWorldSection", props: { name: "valheim_world" } },
  // Straight after the world it belongs to: the modifiers are stored *in* the world, and every
  // server start rewrites them from this section, so reading them as world settings rather than as
  // a detached block of launch flags is the whole point of the rework (TR-5316).
  { component: "ConfSection", props: { name: "world_modifiers" } },
  // Kept high on the page rather than at the bottom: picking a mod loader changes which tabs exist
  // and reinstalls the server's files (TR-5324).
  { component: "ConfSection", props: { name: "valheimMods" } },
  { component: "ValheimPlayerListSection", props: { name: "adminList" } },
  { component: "ValheimPlayerListSection", props: { name: "permittedList" } },
  { component: "ValheimPlayerListSection", props: { name: "bannedList" } },
];

const createMenu = (plusMod: boolean): Menu => {
  const menu = defaultProfile().menu;
  // menu[0].items = [status, settings, fileManager] — insert V+ between settings and fileManager
  if (plusMod) {
    menu[0].items.splice(2, 0, valheimPlusMenuItem);
  }
  // Save Backups + Save Import (TR-5318) sit directly below File Manager, matching Palworld's and
  // SCUM's ordering, with Logs kept last. Labels come from the generic ngp/route/* keys.
  menu[0].items.push(
    new MenuItem(
      "save-backups",
      faClockRotateLeft,
      asyncRoute(() => import("../components/views/valheim/SaveBackups.vue")),
    ),
    new MenuItem(
      "save-import",
      faFileImport,
      asyncRoute(() => import("../components/views/valheim/SaveImport.vue")),
    ),
  );
  menu[0].items.push(commonMenuItems.logs);
  // Mods (TR-5319) sits below Logs with its own sub-routes, the same shape as Project Zomboid's.
  // Deliberately **not** platform gated: the tab ships on every Valheim SKU, because a mod requiring
  // a client-side install is a per-mod constraint on console players, not a reason to remove modding
  // from a console server (TR-5323). BepInEx is injected by Doorstop and works with `-crossplay`.
  menu[0].items.push(
    new MenuItem(
      "mods",
      faPuzzlePiece,
      asyncRoute(() => import("../components/views/config/valheim/Mods.vue")),
      [
        {
          route: "manage",
          component: asyncRoute(() => import("../components/views/mods/manage/ModsManage.vue")),
        },
        {
          route: "settings",
          component: asyncRoute(() => import("../components/views/config/valheim/ModSettings.vue")),
        },
      ],
    ),
  );
  return menu;
};

const join = () => {
  window.open(t("ngp/settings/section/valheim_wiki/link"), "_blank", "noopener,noreferrer");
};

/** The resolved mod model the backend computes on every config read (TR-5319 Phase 3). */
export function useValheimMods() {
  const service = useServiceStore();
  const profile = computed(() => service.ctx.profile as ValheimProfileNamespace | undefined);
  return computed(() => (profile.value as unknown as { valheimMods?: ValheimModsInfo } | undefined)?.valheimMods);
}

/**
 * Selection surface for the shared mod components.
 *
 * The selection is a list of **repo keys** in `virtual.mods` — Valheim has no Steam Workshop, so a
 * numeric id would be a surrogate with nothing behind it. Everything the shared mod components need
 * (`storeTitle`, `installedMods`, `addMod`, `removeMod`) is provided here, keyed on `repoKey`, plus
 * the parts that are genuinely Valheim-specific:
 *
 * * the dependency relation in both directions, which Thunderstore gives us exactly;
 * * modpacks, which are a real package here rather than a Steam collection;
 * * the per-mod "your console friends cannot install this" warning.
 */
export function useMods() {
  const { buildProperty } = usePropertyBuilder();
  const service = useServiceStore();

  const profile = computed(() => service.ctx.profile as ValheimProfileNamespace | undefined);
  const selectedWorkshopItems = computed(() => profile.value?.selectedWorkshopItems ?? []);
  const selection = buildProperty<Array<string>>("virtual", "mods", []);
  const mods = useValheimMods();

  /**
   * The game version this server's branch runs (TR-5406), or `null` when the branch is not mapped.
   *
   * `null` is a first-class answer and everything downstream treats it as "show nothing": a branch we
   * cannot pin to a game version (Steam's moving `public-test`, or one added since this shipped) gets
   * the tab exactly as it was, rather than mod ratings against a version we are guessing at.
   */
  const era = computed(() => mods.value?.era ?? null);

  // ⚠️ Deliberately **not** `useSteamWorkshop`, despite its `modPrefix` support. That composable
  // matches a stored value to a catalogue row by stripping the prefix and comparing the remainder to
  // `mod.id` — which works for Palworld, where the stored key ends in the numeric Workshop id, and
  // cannot work here: a Valheim key ends in `<namespace>_<slug>` while `id` is only a surrogate from
  // the table's primary key. The installed list came back empty for exactly that reason.
  //
  // Reordering is not offered either: BepInEx resolves plugin load order itself via
  // `BepInDependency`, so unlike Project Zomboid's `Mods=` the order of this list carries no meaning.
  const installedMods = computed(() =>
    selection.value
      .map((repoKey) => selectedWorkshopItems.value.find((mod) => mod?.repoKey === repoKey))
      .filter((mod): mod is SteamWorkshopItem => mod != null),
  );

  /**
   * Add exactly the mod the customer picked.
   *
   * 🚨 Deliberately **not** its dependencies. An earlier version resolved the closure here and
   * installed it, which is friendlier but is not what Project Zomboid does — and Project Zomboid is
   * the reference implementation QA compares this tab against. There, a missing dependency becomes a
   * notice with an *Add* button and the customer decides. `ModIssues` does the same here, and the
   * ticket's "pull required mods in automatically" is that one click.
   */
  async function addMod(mod: SteamWorkshopItem) {
    if (!mod.repoKey || selection.value.includes(mod.repoKey)) {
      return;
    }
    selection.value = [...selection.value, mod.repoKey];
    // Push the catalogue row in as well, so the card renders immediately instead of only after the
    // save that makes the backend resolve it.
    selectedWorkshopItems.value.push(mod);

    if (mod.valheim?.isModpack) {
      await expandModpack(mod.repoKey);
    }
  }

  /**
   * Install a modpack's contents along with the pack.
   *
   * 🚨 The one place where adding something adds more than itself, and it is the Project Zomboid
   * behaviour rather than an exception to it: PZ expands a pasted Steam **collection** into its
   * individual items at the moment it is added, precisely because a collection is a list of mods and
   * not a mod. A Thunderstore modpack is that same thing — a manifest with no content of its own — so
   * it expands, while an ordinary mod's dependencies are offered with an *Add* button and never
   * forced.
   *
   * Direct contents only, and only what the customer does not already have: a member they removed on
   * purpose earlier is re-added by this, which is correct (they asked for the pack again), but a
   * member's own dependencies are its business and get the usual notice.
   */
  async function expandModpack(packRepoKey: string) {
    const rsid = service.ctx.service?.config?.rsid;
    if (!rsid) {
      return;
    }
    const resolved = await useValheimModsStore().refreshPending(rsid.id, rsid.region, [...selection.value]);

    // 🚨 Everything below re-reads the selection *after* the await. The customer can remove the pack
    // or add one of its mods while the request is in flight, and acting on the pre-await value would
    // re-add what they just removed or duplicate a repo key — which the next save would install.
    const current = selection.value;
    if (!current.includes(packRepoKey)) {
      return;
    }
    const known = new Set(current);
    const contents = resolved.missing.filter(
      (entry) => entry.requiredByKeys.includes(packRepoKey) && !known.has(entry.repoKey),
    );
    if (!contents.length) {
      return;
    }
    // 🚨 One assignment. `selection` is a computed over the service store, so appending in a loop
    // reads back a value the previous write has not landed in yet and the last write wins — which is
    // how a two-dependency mod once arrived with one.
    selection.value = [...current, ...contents.map((entry) => entry.repoKey)];
    for (const entry of contents) {
      addSelectedItem(placeholderItem(entry));
    }
  }

  function removeFromSelection(repoKey?: string | null) {
    if (repoKey) {
      selection.value = selection.value.filter((key) => key !== repoKey);
    }
  }

  const base = {
    storeTitle: computed(() => t("ngp/valheim_mods/store")),
    /** The live selection, so a notice can disappear as soon as its *Add* button has been used. */
    installedRepoKeys: computed(() => selection.value),
    installedMods,
    addMod,
    removeMod: (mod: SteamWorkshopItem) => removeFromSelection(mod.repoKey),
  };

  /** The backend's entry for a mod, or undefined for a search result it has never seen. */
  function entryOf(repoKey?: string | null): ValheimSelectedMod | undefined {
    return repoKey ? (mods.value?.selected ?? []).find((entry) => entry?.repoKey === repoKey) : undefined;
  }

  function nameOf(repoKey: string) {
    return entryOf(repoKey)?.name ?? repoKey.replace(MOD_PREFIX, "").replace("_", "-");
  }

  /**
   * Which catalogue produced a mod's build, looked up by `namespace-slug` (TR-5407).
   *
   * Only the installed selection can answer this: a dependency reference names a mod we may never
   * have fetched a catalogue row for. Returns undefined when unknown, so {@link packageUrl} falls
   * back to Thunderstore rather than guessing Hexium.
   */
  /**
   * Which catalogue produced this mod's published build.
   *
   * The installed entry wins over the browse row: the browse row describes the catalogue's current
   * head, the installed entry describes what is actually on the server, and during the window
   * between a new ranking and the daemon's next update cycle those differ.
   */
  function sourceOf(mod: SteamWorkshopItem): string {
    return entryOf(mod.repoKey)?.source ?? mod.valheim?.source ?? "thunderstore";
  }

  function sourceOfFullName(fullName?: string | null): string | undefined {
    if (!fullName) {
      return undefined;
    }
    return (mods.value?.selected ?? []).find((entry) => entry?.fullName === fullName)?.source ?? undefined;
  }

  /**
   * The declared dependency graph of the current selection, both directions, built in one pass.
   *
   * 🚨 Built from what each mod **declares** (the catalogue's `dependencies`), not from the recorded
   * provenance. Origins only say why a mod was *added*, so a library the customer picked themselves
   * has no `addedFor` — and deleting `JsonDotNET` while EpicLoot required it went through without a
   * word. Thunderstore dependencies are exact strings, so the real edge is always knowable.
   *
   * Uses the live selection rather than the last config read: a mod deleted a moment ago must stop
   * counting as a dependent, or the panel warns about breaking something that is already gone.
   */
  const dependencyIndex = computed(() => {
    const dependents = new Map<string, Array<{ workshopId: string; name: string }>>();
    const requirements = new Map<string, Array<{ workshopId: string; name: string }>>();
    const selected = new Set(selection.value);
    const byFullName = new Map<string, SteamWorkshopItem>();

    for (const mod of selectedWorkshopItems.value) {
      if (mod?.valheim && mod.repoKey && selected.has(mod.repoKey)) {
        byFullName.set(mod.valheim.fullName, mod);
      }
    }

    function link(from: SteamWorkshopItem, to: SteamWorkshopItem) {
      if (!from.repoKey || !to.repoKey || from.repoKey === to.repoKey) {
        return;
      }
      dependents.set(to.repoKey, [
        ...(dependents.get(to.repoKey) ?? []),
        { workshopId: from.repoKey, name: from.name },
      ]);
      requirements.set(from.repoKey, [
        ...(requirements.get(from.repoKey) ?? []),
        { workshopId: to.repoKey, name: to.name },
      ]);
    }

    for (const mod of byFullName.values()) {
      for (const reference of mod.valheim?.dependencies ?? []) {
        if (isLoaderReference(reference)) {
          continue;
        }
        const target = byFullName.get(stripVersion(reference));
        if (target) {
          link(mod, target);
        }
      }
      // A modpack does not always *declare* everything it installed (a member can arrive through a
      // deeper level), so its recorded membership counts as an edge too.
      const entry = entryOf(mod.repoKey);
      for (const key of entry?.isModpack ? entry.members ?? [] : []) {
        const member = selectedWorkshopItems.value.find((other) => other?.repoKey === key);
        if (member && selected.has(key)) {
          link(mod, member);
        }
      }
    }
    return { dependents, requirements };
  });

  /** Selected mods that require this one, or that installed it as part of a pack. */
  function dependentsOf(repoKey?: string | null) {
    if (!repoKey) {
      return [];
    }
    // De-duplicated: a modpack can both declare a member and list it in `members`.
    const seen = new Set<string>();
    return (dependencyIndex.value.dependents.get(repoKey) ?? []).filter((entry) => {
      if (seen.has(entry.workshopId)) {
        return false;
      }
      seen.add(entry.workshopId);
      return true;
    });
  }

  /** Selected mods this one requires (or, for a pack, brings with it). */
  function requirementsOf(repoKey?: string | null) {
    if (!repoKey) {
      return [];
    }
    const seen = new Set<string>();
    return (dependencyIndex.value.requirements.get(repoKey) ?? []).filter((entry) => {
      if (seen.has(entry.workshopId)) {
        return false;
      }
      seen.add(entry.workshopId);
      return true;
    });
  }

  /** A mod's declared dependencies as `namespace-slug`, loader excluded. */
  function declaredDependencies(mod: SteamWorkshopItem) {
    return (mod.valheim?.dependencies ?? [])
      .filter((reference) => !isLoaderReference(reference))
      .map((reference) => stripVersion(reference));
  }

  /**
   * How a mod rates against the game version this server runs (TR-5406).
   *
   * 🚨 The backend's verdict wins wherever there is one. Only it knows the dependency capping — a mod
   * rebuilt for 1.0 that needs a library which was not still cannot load — and re-deriving the tier
   * in TypeScript would drop that and quietly promote the mod back to "fine". `classify()` is only
   * for browse results, which the backend has never resolved.
   */
  function compatibilityOf(mod: SteamWorkshopItem): string {
    return entryOf(mod.repoKey)?.compatibility ?? classifyMod(mod, era.value);
  }

  /** The compatibility badge, or nothing at all when we have no business claiming anything. */
  function compatibilityTag(mod: SteamWorkshopItem): ModTag | undefined {
    const version = era.value?.label ?? "";
    const entry = entryOf(mod.repoKey);
    // Named rather than counted: "requires X, which is outdated" is the actionable half, and there
    // is never more than a handful.
    const blockers = (entry?.blockedBy ?? []).filter((key): key is string => !!key).map(nameOf);
    const updatedOn = formatDay(releaseDay(mod), i18n.global.locale.value);

    switch (compatibilityOf(mod)) {
      case COMPAT.ready:
        return {
          label: t("ngp/valheim_mods/compat/ready", { version }),
          icon: faCircleCheck,
          level: "success",
          tooltip: t("ngp/valheim_mods/compat/ready_tooltip", { version }),
        };
      case COMPAT.probablyOutdated:
        return {
          label: t("ngp/valheim_mods/compat/outdated"),
          icon: faTriangleExclamation,
          level: "warning",
          tooltip: blockers.length
            ? t("ngp/valheim_mods/compat/blocked_tooltip", { mods: blockers.join(", ") })
            : t("ngp/valheim_mods/compat/outdated_tooltip", { date: updatedOn, version }),
        };
      case COMPAT.likelyBroken:
        return {
          label: t("ngp/valheim_mods/compat/broken"),
          icon: faTriangleExclamation,
          level: "danger",
          tooltip: blockers.length
            ? t("ngp/valheim_mods/compat/blocked_tooltip", { mods: blockers.join(", ") })
            : t("ngp/valheim_mods/compat/broken_tooltip", { date: updatedOn, version }),
        };
      default:
        // `likely_ok` and `unknown` both say nothing. A badge on every card that is merely *not* a
        // problem is a badge nobody reads, and it would bury the two that matter.
        return undefined;
    }
  }

  function modTags(mod: SteamWorkshopItem): ModTag[] {
    const tags: ModTag[] = [];
    const entry = entryOf(mod.repoKey);
    const meta = mod.valheim;

    // First, because on release week it is the one thing that decides whether the server starts.
    const compatibility = compatibilityTag(mod);
    if (compatibility) {
      tags.push(compatibility);
    }
    if (meta?.isModpack) {
      tags.push({ label: t("ngp/valheim_mods/tag/modpack"), icon: faWandMagicSparkles });
    }
    if (entry?.addedAs === "dependency") {
      const addedFor = (entry.addedFor ?? []).map(nameOf);
      tags.push({
        label: t("ngp/valheim_mods/tag/dependency"),
        tooltip: addedFor.length ? t("ngp/valheim_mods/tag/added_for", { mods: addedFor.join(", ") }) : undefined,
      });
    }
    const dependents = dependentsOf(mod.repoKey);
    if (dependents.length) {
      tags.push({
        label: t("ngp/valheim_mods/tag/required_by", dependents.length),
        icon: faLink,
        tooltip: dependents.map((other) => other.name).join(", "),
      });
    }
    // What this mod pulls in, straight from the catalogue — the pre-add question, and the same tag
    // Project Zomboid puts on its browse cards. Only for a mod that is not in the list yet: once it
    // is, its dependencies are in the list too and "Required by" above says it from the other side.
    const requires = declaredDependencies(mod);
    if (!selection.value.includes(mod.repoKey ?? "") && requires.length) {
      tags.push({
        label: meta?.isModpack
          ? t("ngp/valheim_mods/tag/contains", requires.length)
          : t("ngp/valheim_mods/tag/requires", requires.length),
        icon: faLink,
        tooltip: requires.join(", "),
      });
    }
    // 🚨 The console constraint, per mod. A mod that needs a client-side install cannot be installed
    // by Xbox, PS5 or Switch 2 players at all, so it locks them out of the server. That is the real
    // limit — not the tab, and not crossplay, which BepInEx works fine with.
    if (mod.requiresClientMod) {
      tags.push({ label: t("ngp/valheim_mods/tag/requires_client_mod"), icon: faTriangleExclamation });
    } else if (mod.isServer !== true) {
      // Neither category declared by the author. Not a defect and not rare (roughly two thirds of the
      // catalogue), so it is stated as unknown rather than guessed in either direction.
      tags.push(t("ngp/valheim_mods/tag/side_unknown"));
    }
    if (meta?.isDeprecated) {
      tags.push({ label: t("ngp/valheim_mods/tag/deprecated"), icon: faTriangleExclamation });
    }
    // ⚠️ Files, not loading. Measured on a live service: saving the list deploys the packages
    // immediately, because the daemon runs an update on the config push — so this tag is for the case
    // where that has *not* happened (a stopped service, an unreachable daemon), not for the normal
    // path. What a restart is needed for is BepInEx chainloading the plugins, which the section-level
    // hint says once instead of on every card.
    if (mod.repoKey && (mods.value?.pendingInstall ?? []).includes(mod.repoKey)) {
      tags.push(t("ngp/valheim_mods/tag/pending_install"));
    }
    return tags;
  }

  /** Prose for the manage list, where mods get deleted and "why is this here" matters most. */
  function modNotes(mod: SteamWorkshopItem) {
    const notes: string[] = [];
    const entry = entryOf(mod.repoKey);

    // The manage list is where mods get deleted, so the compatibility state belongs here in full
    // prose rather than as the card's abbreviated badge. Mods that are merely fine say nothing.
    const compatibility = compatibilityOf(mod);
    if (compatibility === COMPAT.probablyOutdated || compatibility === COMPAT.likelyBroken) {
      const blockers = (entry?.blockedBy ?? []).filter((key): key is string => !!key).map(nameOf);
      if (blockers.length) {
        notes.push(t("ngp/valheim_mods/compat/note_blocked", { mods: blockers.join(", ") }));
      } else {
        notes.push(
          t(
            compatibility === COMPAT.likelyBroken
              ? "ngp/valheim_mods/compat/note_broken"
              : "ngp/valheim_mods/compat/note_outdated",
            { date: formatDay(releaseDay(mod), i18n.global.locale.value), version: era.value?.label ?? "" },
          ),
        );
      }
    }

    const dependents = dependentsOf(mod.repoKey);
    if (dependents.length) {
      notes.push(t("ngp/valheim_mods/note/required_by", { mods: dependents.map((other) => other.name).join(", ") }));
    }
    if (entry?.isModpack && entry.members?.length) {
      notes.push(t("ngp/valheim_mods/note/pack_members", { count: entry.members.length }));
    } else {
      // Both directions, as Project Zomboid's manage list does. Skipped for a modpack, whose
      // contents are counted by the line above.
      const requirements = requirementsOf(mod.repoKey);
      if (requirements.length) {
        notes.push(t("ngp/valheim_mods/note/requires", { mods: requirements.map((other) => other.name).join(", ") }));
      }
    }
    // Provenance only when the live graph does not already say it: "added as a dependency" next to
    // "required by X" is the same sentence twice. What it uniquely answers is the case where whatever
    // it was added for is gone — exactly when the customer wonders why this is still here.
    if (entry?.addedAs === "dependency" && !dependents.length) {
      notes.push(t("ngp/valheim_mods/note/unused_dependency"));
    }
    return notes;
  }

  /**
   * What the dialog's external link is called, per mod (TR-5407).
   *
   * `storeTitle` is "Thunderstore" and is wrong for a Hexium winner: the href already resolves to
   * hexium.gg, so a button reading "Thunderstore" describes a page it does not open.
   *
   * Uses {@link formatModSource}, the same formatter as the card's `Source:` line, so the dialog and
   * the tile call the catalogue by the same name.
   */
  function modStoreTitle(mod: SteamWorkshopItem) {
    return formatModSource(sourceOf(mod)) ?? undefined;
  }

  function modHint(mod: SteamWorkshopItem) {
    if (mod.requiresClientMod) {
      return t("ngp/valheim_mods/hint/requires_client_mod");
    }
    if (mod.isServer !== true) {
      return t("ngp/valheim_mods/hint/side_unknown");
    }
    return undefined;
  }

  /**
   * Dependencies and dependents in the mod info dialog, both directions.
   *
   * Dependencies come from the catalogue, so they are known while *browsing* too — which is the
   * pre-add question. Dependents can only be answered for a mod that is in the list.
   */
  function modRelations(mod: SteamWorkshopItem): ModRelation[] {
    const relations: ModRelation[] = [];
    const references = declaredDependencies(mod);

    if (references.length) {
      relations.push({
        key: "dependencies",
        title: mod.valheim?.isModpack
          ? t("ngp/valheim_mods/dialog/contains")
          : t("ngp/valheim_mods/dialog/dependencies"),
        entries: references.map((fullName) => {
          const installed = (mods.value?.selected ?? []).find((entry) => entry?.fullName === fullName);
          return {
            label: fullName,
            url: packageUrl(fullName, sourceOfFullName(fullName)),
            // 🚨 States where the mod stands, it does not promise anything. It used to read
            // "Installed automatically", which was untrue at the moment it was shown: nothing had
            // been added yet. Adding a mod now really does pull its dependencies in (see `addMod`),
            // so by the time a mod is in the list this note is simply gone.
            note: installed ? undefined : t("ngp/valheim_mods/dialog/not_in_list"),
          };
        }),
      });
    }

    const dependents = dependentsOf(mod.repoKey);
    if (dependents.length) {
      relations.push({
        key: "dependents",
        title: t("ngp/valheim_mods/dialog/dependents"),
        entries: dependents.map((other) => ({
          label: other.name,
          url: packageUrl(entryOf(other.workshopId)?.fullName, entryOf(other.workshopId)?.source),
        })),
      });
    }

    const entry = entryOf(mod.repoKey);
    if (entry?.isModpack && entry.members?.length) {
      relations.push({
        key: "members",
        title: t("ngp/valheim_mods/dialog/pack_members"),
        entries: entry.members.map((key) => ({
          label: nameOf(key),
          url: packageUrl(entryOf(key)?.fullName, entryOf(key)?.source),
        })),
      });
    }

    // What each catalogue currently offers (TR-5407). Only worth a block when there is more than
    // one: for a mod that exists in a single place this would be a section restating the card.
    //
    // This is the half the badge cannot show. "From Hexium" alone leaves the obvious question
    // unanswered — is Thunderstore behind, or does it not have this mod at all? — and the answer is
    // the thing a customer needs when deciding whether the source explains what they are seeing.
    const sources = (mod.valheim?.sources ?? []).filter(
      (snapshot): snapshot is NonNullable<typeof snapshot> => !!snapshot,
    );
    if (sources.length > 1) {
      relations.push({
        key: "sources",
        title: t("ngp/valheim_mods/dialog/sources"),
        entries: sources.map((snapshot) => {
          const published = snapshot.source === sourceOf(mod);
          const day = formatDay(snapshot.dateUpdated, i18n.global.locale.value);
          return {
            label: t(`ngp/valheim_mods/source/${snapshot.source}`),
            url: snapshot.url ?? packageUrl(mod.valheim?.fullName, snapshot.source),
            // Version and date together, because either alone invites the wrong conclusion: a higher
            // version that is two years old is not the newer build, and a recent date on a lower
            // version is not a reason to switch.
            note: [
              snapshot.versionNumber ? `v${snapshot.versionNumber}` : null,
              day,
              published ? t("ngp/valheim_mods/source/published") : null,
            ]
              .filter(Boolean)
              .join(" · "),
          };
        }),
      });
    }
    return relations;
  }

  /**
   * Ask before removing something other mods need, and offer to take a pack's members with it.
   *
   * The backend reports the breakage too, but only after the save — which means finding out by
   * reading an error next to a list you have already changed. Thunderstore dependencies are exact, so
   * the answer is known before the click.
   */
  async function removeMod(mod: SteamWorkshopItem) {
    const entry = entryOf(mod.repoKey);
    const dependents = dependentsOf(mod.repoKey);

    if (dependents.length) {
      const confirmed = await useDialogStore().createDialog({
        component: markRaw(DialogConfirm),
        props: {
          headerIcon: faTriangleExclamation,
          headerText: t("ngp/valheim_mods/remove/confirm_title"),
          contentText: t("ngp/valheim_mods/remove/confirm_text", {
            mod: mod.name,
            dependents: dependents.map((other) => other.name).join(", "),
          }),
        },
      });
      if (!confirmed) {
        return;
      }
    }

    if (entry?.isModpack && entry.members?.length) {
      // 🚨 One question, and both buttons do the obvious thing: confirm removes the pack *and* the
      // mods it installed, cancel removes nothing. `DialogConfirm` has no custom button labels, so an
      // earlier "remove the members as well?" phrasing made cancel remove the pack but keep its
      // members — an outcome nobody asked for and nobody would predict from a button marked CANCEL.
      const confirmed = await useDialogStore().createDialog({
        component: markRaw(DialogConfirm),
        props: {
          headerIcon: faTriangleExclamation,
          headerText: t("ngp/valheim_mods/remove/pack_title"),
          contentText: t("ngp/valheim_mods/remove/pack_text", { mod: mod.name, count: entry.members.length }),
        },
      });
      if (!confirmed) {
        return;
      }
      // Only members that arrived *as* part of this pack. A member the customer had also picked
      // themselves keeps its manual origin backend-side and stays — removing someone's own choice
      // because a pack happened to include it is the surprise this whole flow avoids.
      const removable = new Set(entry.members.filter((key) => entryOf(key)?.addedAs === "dependency"));
      removable.add(mod.repoKey!);
      selection.value = selection.value.filter((key) => !removable.has(key));
      return;
    }

    base.removeMod(mod);
  }

  /**
   * Add one missing dependency, from the issues panel's *Add* button.
   *
   * Mirrors Project Zomboid's `addDependency`: the notice carries the repo key and the name, which is
   * all the card needs, so no catalogue round trip is required to act on it.
   */
  function addDependency(entry: { repoKey: string; name: string; fullName?: string | null }) {
    if (!entry.repoKey || selection.value.includes(entry.repoKey)) {
      return;
    }
    selection.value = [...selection.value, entry.repoKey];
    addSelectedItem(placeholderItem(entry));
  }

  /**
   * Add a row to the profile's resolved-items list, so its card renders before the next config read.
   *
   * ⚠️ Guarded on the array actually existing. `selectedWorkshopItems` is a computed with a `?? []`
   * fallback, and pushing into that fallback mutates a throwaway array — the mod would sit in the
   * selection and render nowhere until the next read. Nothing to do in that case: the read fills it
   * in, and a missing card is better than a mutation that looks like it worked.
   */
  function addSelectedItem(item: SteamWorkshopItem) {
    const items = profile.value?.selectedWorkshopItems;
    if (items) {
      items.push(item);
    }
  }

  /**
   * A stand-in row for a mod we know only by name and repo key.
   *
   * Exactly what Project Zomboid's `addDependency` builds, and for the same reason: a notice carries a
   * name, not a catalogue row, and without this the mod would sit in the selection and render nowhere
   * until the next config read.
   */
  function placeholderItem(entry: { repoKey: string; name: string; fullName?: string | null; source?: string | null }) {
    return {
      id: 0,
      name: entry.name,
      appId: 0,
      itemType: SteamWorkshopItemType.File,
      logo: { url: DEFAULT_MOD_IMAGE },
      links: { websiteUrl: packageUrl(entry.fullName, entry.source) ?? THUNDERSTORE_URL },
      repoKey: entry.repoKey,
      revisionId: 0,
      // ⚠️ Empty, not `new Date(0)`. Project Zomboid's placeholder uses the epoch and its card then
      // reads "Created: 1/1/1970"; an unparsable date makes `Mod.vue` hide the line instead, which is
      // the honest rendering of "we do not know yet".
      dateCreated: "",
      lastUpdateTime: 0,
    } as SteamWorkshopItem;
  }

  /**
   * Installed mods in the worst tier — what the page-level warning is built from.
   *
   * 🚨 Derived from the **live** selection, not from the backend's `outdated` list. That list
   * describes the selection as it was at the last config read, so a mod removed a moment ago would
   * keep the banner up and a mod added a moment ago would not raise it. `compatibilityOf` still
   * prefers the backend's verdict per mod, so the dependency capping is not lost.
   */
  const outdatedMods = computed(() =>
    installedMods.value.filter((mod) => compatibilityOf(mod) === COMPAT.likelyBroken),
  );

  /** Drop several mods at once — the issues panel's "remove the unused dependencies" action. */
  function removeUnused(repoKeys: string[]) {
    const drop = new Set(repoKeys);
    selection.value = selection.value.filter((key) => !drop.has(key));
  }

  return {
    ...base,
    modTags,
    addDependency,
    removeUnused,
    nameOfRepoKey: nameOf,
    modHint,
    modNotes,
    modRelations,
    modStoreTitle,
    removeMod,
    dependentsOf,
    requirementsOf,
    era,
    compatibilityOf,
    outdatedMods,
    sourceOf,
  };
}

/** ``namespace-slug-version`` → ``namespace-slug``. Mirrors the backend's `strip_version`. */
const VERSION_TAIL = /^\d+(?:\.\d+)*(?:-[0-9A-Za-z.-]+)?(?:\+[0-9A-Za-z.-]+)?$/;

/**
 * `namespace-slug-version` → `namespace-slug`. Mirrors `strip_version()` in
 * `decline/game_profiles/valheim_mods.py`; keep the two in step.
 *
 * Cutting at the last dash only works while the version is exactly one segment. Thunderstore
 * enforces `x.y.z` so it always was, but Hexium accepts full semver, and a prerelease spans several
 * dash segments: `ArgusMagnus-ServersideQoL-2.0.11-beta.1` would become
 * `ArgusMagnus-ServersideQoL-2.0.11-beta`, which matches no mod, so the dependency renders as one we
 * do not offer.
 *
 * Walk the dashes from the right and cut at the first suffix that looks like a version. Returns the
 * reference untouched when nothing matches, rather than guessing.
 */
export function stripVersion(reference: string) {
  const trimmed = reference.trim();
  let index = trimmed.length;
  for (;;) {
    index = trimmed.lastIndexOf("-", index - 1);
    if (index <= 0) return trimmed;
    if (VERSION_TAIL.test(trimmed.slice(index + 1))) return trimmed.slice(0, index);
  }
}

function isLoaderReference(reference: string) {
  return stripVersion(reference).endsWith("BepInExPack_Valheim");
}

/**
 * The mod's page on the catalogue that produced it (TR-5407).
 *
 * `source` is optional because most callers name a mod by a *dependency reference*, and a reference
 * says nothing about where that mod's build came from. Thunderstore is the right default there: it
 * carries 11k packages to Hexium's 926, so it is overwhelmingly the likely home, and a wrong guess
 * costs a link to a page that lists the mod under a different owner rather than a dead one.
 *
 * Pass `source` whenever it is known — a catalogue row always knows it — and a Hexium-exclusive mod
 * then links to Hexium instead of a Thunderstore URL that 404s.
 */
export function packageUrl(fullName?: string | null, source?: string | null) {
  if (!fullName) {
    return undefined;
  }
  // ⚠️ Split on the *last* dash, not the first: `sinai-dev-UnityExplorer` is a real package whose
  // namespace contains one.
  const index = fullName.lastIndexOf("-");
  if (index <= 0) {
    return undefined;
  }
  const namespace = fullName.slice(0, index);
  const slug = fullName.slice(index + 1);
  if (source === "hexium") {
    return `https://valheim.hexium.gg/mods/${namespace}/${slug}`;
  }
  return `https://valheim.thunderstore.io/package/${namespace}/${slug}/`;
}

/** @deprecated Use {@link packageUrl}, which knows that Thunderstore is not the only catalogue. */
export const thunderstoreUrl = packageUrl;

export default (ctx: ProfileCtx): Partial<GameProfile<SteamWorkshopItem, IUseMods<SteamWorkshopItem>>> => {
  const plusMod: boolean = ctx.profile?.publicConfigs?.virtual?.valheimMods == "valheimPlus";
  // The tab renders the vplus_* sections, which only exist on rows whose form configuration has
  // been migrated. Showing it against an unmigrated row would give an empty page, so key the menu
  // item on the sections actually being there rather than on the mod selection alone.
  const hasSplitSections = !!ctx.profile?.configDefinition?.sections?.vplus_valheim_plus;

  return {
    menu: createMenu(plusMod && hasSplitSections),
    configSections,
    useMods,
    // The generic "not updated in 60 days, possibly incompatible" hint has no data to run on here:
    // `game_valheim_mods` carries no upstream date, only the row's own ingest timestamps, which every
    // catalogue run touches. It would therefore never fire — a silently dead warning, which reads as
    // "nothing here is stale". Thunderstore's own `is_deprecated` is authoritative and is shown as a
    // tag instead (TR-5319 Phase 6).
    staleModWarning: false,
    join: {
      buttonTextKey: "ngp/settings/section/valheim_wiki",
      joinFn: join,
    },
  };
};
